import React from "react";
import { CSSObjectWithLabel, GroupBase, OptionProps, StylesConfig, ValueContainerProps, components } from "react-select";

// elements
import Checkbox from "../Checkbox";

// interfaces
import { SingleSelectOption } from "./ISelect";

const BASE_SELECT_WIDTH = "170px";

interface IState {
  isFocused: boolean;
  isDisabled: boolean;
  isMulti: boolean;
}

export const generateSelectStyles = (
  { fullWidth, hasError }: { fullWidth?: boolean, hasError: boolean }
): StylesConfig<SingleSelectOption, boolean, GroupBase<SingleSelectOption>> => {
  const getContainerStyles = ({ isFocused, isDisabled }: IState) => {
    switch (true) {
      case hasError:
        return {
          border: "1px solid var(--rose-color-500)",
          boxShadow: "0px 0px 0px 3px rgb(255, 228, 230)"
        };
      case isFocused && !isDisabled:
        return {
          border: "1px solid var(--primary-color-500)",
          boxShadow: "0px 0px 0px 3px rgba(231, 248, 247)"
        };
      default:
        return {
          border: "1px solid var(--secondary-color-300)",
          boxShadow: "0px 1px 2px rgba(31, 41, 55, 0.08)"
        };
    }
  };

  const getContainerHoveredStyles = ({ isFocused, isDisabled }: IState) => {
    switch (true) {
      case hasError:
        return {
          border: "1px solid var(--rose-color-500)",
          boxShadow: "0px 0px 0px 3px rgb(255, 228, 230)"
        };
      case isFocused && !isDisabled:
        return {
          border: "1px solid var(--primary-color-500)",
          boxShadow: "0px 0px 0px 3px rgba(231, 248, 247)"
        };
      case isDisabled:
        return {
          border: "1px solid var(--gray-color-400)",
          boxShadow: "0px 1px 2px rgba(31, 41, 55, 0.08)"
        };
      default:
        return {
          border: "1px solid var(--gray-color-400)",
          boxShadow: "0px 0px 0px 3px rgba(229, 231, 235)"
        };
    }
  };

  return {
    container: (stylesProps: CSSObjectWithLabel, state: IState) => ({
      ...stylesProps,
      backgroundColor: !state.isDisabled ? "var(--white-color-500)" : "var(--secondary-color-200)",
      width: fullWidth ? "100%" : BASE_SELECT_WIDTH,
      borderRadius: "4px"
    }),
    control: (stylesProps: CSSObjectWithLabel, state: IState) => ({
      ...stylesProps,
      ...getContainerStyles(state),
      backgroundColor: "transparent",
      borderRadius: "4px",
      outline: "none",
      width: "100%",
      minHeight: "36px",
      height: state.isMulti ? "auto" : "36px",
      color: "#969fa4",
      cursor: state.isDisabled ? "not-allowed" : "default",
      pointerEvents: "all",
      opacity: state.isDisabled ? "0.6" : "1",
      transition: "all .3s",
      "&:hover": { ...getContainerHoveredStyles(state) }
    }),
    valueContainer: (
      stylesProps: CSSObjectWithLabel, state: ValueContainerProps<SingleSelectOption,
      boolean, GroupBase<SingleSelectOption>>
    ) => ({
      ...stylesProps,
      fontFamily: "var(--main-font-family)",
      height: state.isMulti ? "auto" : "36px",
      top: "0",
      padding: "0 10px",
      color: "var(--secondary-color-500)"
    }),
    singleValue: (stylesProps: CSSObjectWithLabel) => ({
      ...stylesProps,
      fontFamily: "var(--main-font-family)"
    }),
    menu: (stylesProps: CSSObjectWithLabel) => ({
      ...stylesProps,
      padding: "8px",
      color: "rgba(255, 255, 255, 0.6)",
      background: "var(--white-color-500)",
      width: fullWidth ? "100%" : "max-content",
      minWidth: BASE_SELECT_WIDTH,
      borderRadius: "4px",
      maxWidth: fullWidth ? "100%" : "250px",
      zIndex: 10,
      boxShadow: "0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(31, 41, 55, 0.1)"
    }),
    menuList: (stylesProps: CSSObjectWithLabel) => ({
      ...stylesProps,
      padding: "0px"
    }),
    option: (stylesProps: CSSObjectWithLabel, { isDisabled, data }) => ({
      ...stylesProps,
      padding: "8px",
      fontFamily: "var(--main-font-family)",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "20px",
      borderRadius: "2px",
      backgroundColor: "transparent !important",
      color: "var(--secondary-color-500)",
      opacity: isDisabled ? ".5" : "1",
      cursor: isDisabled ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gridGap: "8px",
      "&:hover": { backgroundColor: "var(--gray-color-300) !important" },
      ...((data as { isDivider?: boolean })?.isDivider && {
        position: "relative",
        pointerEvents: "none",
        "&:after": {
          content: "''",
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          height: "1px",
          width: "100%",
          backgroundColor: "var(--secondary-color-300)"
        }
      }),
    }),

    indicatorsContainer: (stylesProps: CSSObjectWithLabel) => ({
      ...stylesProps,
      height: "36px"
    })
  };
};

export const ValueContainer = (
  { children, ...props }: ValueContainerProps<SingleSelectOption, boolean, GroupBase<SingleSelectOption>>
) => {
  const [values, input] = children as React.ReactNode[];
  let returnValue = values;

  if (Array.isArray(values)) {
    const firstValue = values[0].props.children;

    returnValue = `${firstValue}${values.length - 1 ? ` +${values.length - 1}` : ""}`;

    if (props.options.length === values.length) {
      returnValue = firstValue;
    }
  }

  return (
    <components.ValueContainer {...props}>
      {returnValue}
      {input}
    </components.ValueContainer>
  )
};

interface IOptionProps extends OptionProps<SingleSelectOption, boolean, GroupBase<SingleSelectOption>> {
  renderOption?: (option: OptionProps<SingleSelectOption, boolean, GroupBase<SingleSelectOption>>) => React.ReactNode;
}

export const Option = (props: IOptionProps) => {
  const { renderOption, ...restProps } = props;

  return (
    <components.Option {...restProps}>
      {
        props.isMulti ? (
          <Checkbox
            checked={props.isSelected}
            onChange={() => null}
            size="small"
            disabled={props.isDisabled}
          />
        ) : null
      }

      <label className="locl-select-label">
        { renderOption ? renderOption(restProps) : props.label }
      </label>
    </components.Option>
  )
};
